
/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae view page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useHistory, useParams } from 'react-router';

import { Link } from 'react-router-dom';

import Typography from '@material-ui/core/Typography';

import { Theme, darken, makeStyles } from '@material-ui/core/styles';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

import classNames from 'classnames';


import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, List, ListItem, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Tooltip } from '@material-ui/core';

import { Field, FormRenderProps, useForm } from 'react-final-form';

import TextFieldWrapper from '../../components/FinalForm/FinalFormControls/TextFieldWrapper';

import AsyncForm from '../../components/FinalForm/AsyncForm';

import arrayMutators from 'final-form-arrays';

import * as Yup from 'yup';

import { useSnackbar } from 'notistack';


import { default as SMRForm } from '../../components/SaeMedicalReviewForm/SaeMedicalReviewForm';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */
import { faDownload } from '@fortawesome/pro-duotone-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@material-ui/lab/Alert';
import { CollapsibleTable, IDataTableProps, ITextFormattingOptions, InstitutionContext, RouteLoading } from '@ngt/online-patient-management-ui';
import { RequestState } from '@ngt/request-utilities';
import { DateTime } from 'luxon';
import { Column } from 'react-table';
import * as Dtos from '../../api/Neon.Dtos';
import SaeBreadcrumbs from '../../components/Breadcrumbs/SaeBreadcrumbs';
import SaeHistory from '../../components/SaeHistory/SaeHistory';
import SaeInformation from '../../components/SaeInformation/SaeInformation';
import { GetFieldLookup } from '../../context/LookupsContext';
import { useSaeById } from '../../store/modules/saereview/sae';
import ToggleButtonGroup from "../../components/OpmsForm/Fields/ToggleButtonGroup";
import LookupContext from '../../context/LookupContext';
import {ToggleButtonGroupWrapper} from "../../components/FinalForm/FinalFormControls/ToggleButtonGroupWrapper";
import {ILookup} from "../../api/Neon.Dtos";
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import PageLayout from 'src/components/Layout/PageLayout';
import SaeNarrativeSummary from '../../components/SaeNarrativeSummary/SaeNarrativeSummary';
import InvalidSaeForm from '../../components/InvalidSaeForm/InvalidSaeForm';
import { ChangeEvent } from 'react';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeViewPageParams {
    patientStudyNumber?: string,
    saeNumber?: string;
    viewMode?: string;
}

interface ISaeViewPageProps {
}

interface IOcGroup<T extends Dtos.ISaeSubform = any> {
    name: string;
    title: string;
    data: T[];
    columns: Array<Column<T>>;
    createRowProps?: IDataTableProps<T>['createRowProps'];
    titleFormattingOptions?: ITextFormattingOptions;
    entityName?: string;
    Before?: React.ComponentType;
}

interface ISaeViewButtonsProps {
    saeStatus?: Dtos.SaeStatus;
    canViewSae?: boolean | null;
    canReviewSae?: boolean | null;
    canAdministerSae?: boolean | null;
    canMedicallyReviewSae?: boolean | null;
}

interface IInvalidSaeFormProps {
    canAdministerSae?: boolean | null;
    canMedicallyReviewSae?: boolean | null;
}

interface IRejectionForm {
    reason?: string;
    rejectedAs?: number;
    followUpNumber?: number;
}
interface IRejectionFormProps extends FormRenderProps<IRejectionForm> {
    onFormSave?: () => void;
}

interface INoteModalProps {
    note: string;
    setNote: React.Dispatch<React.SetStateAction<string>>;
    modalOpen: boolean;
    onCloseClick?: () => void;
    onSave?: () => void;
}

const relatednessPropertyPath = 'lastDoseStudy.drugRelatedEvent'
const medicalReviewRelatednessPropertyPath = 'MedicalReview.Relatedness'
const expectednessPropertyPath = 'MedicalReview.Expectedness'
const treatmentPropertyPath = 'lastDoseStudy.drugName'

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    blueBgColor: {
        backgroundColor: '#d9edf7',
        minHeight: '20px',
        '&:nth-child(odd)': {
            background: darken('#d9edf7', 0.07)
        }
    },
    cellHeight: {
        minHeight: '20px'
    },
    buttonSet: {
        textAlign: 'right',
        marginTop: theme.spacing(3)
    },
    button: {
        marginLeft: theme.spacing(1),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    },
    hidden: {
        display: 'none'
    },
    legend: {
        backgroundColor: darken('#d9edf7',0.07),
        height: '15px',
        width: '15px',
        position: 'absolute',
        top: '50%',
        marginTop: '-7.5px'
    },
    legendContainer: {
        position: 'relative',
        marginTop: theme.spacing(3)
    },
    legendInfo: {
        marginLeft: theme.spacing(3),
        marginTop: theme.spacing(1),
        lineHeight: '1.0!important'
    },
    mb3: {
        marginBottom: theme.spacing(3),
    },
    mt4: {
        marginTop: theme.spacing(4)
    },
    mt3: {
        marginTop: theme.spacing(3),
    },
    mt2: {
        marginTop: theme.spacing(2),
    },
    mt1: {
        marginTop: theme.spacing(1),
    },
    table: {
        marginTop: theme.spacing(2),
        borderTop: '1px',

    },
    tableHeader: {
        fontWeight: 'bold',
        width: '50%'
    },
    tableHeaderColor: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white
    },
    row: {
        '&:nth-child(odd)': {
            background: theme.palette.grey[100]
        }
    },
    tableMedicalReview: {
        border: '1px solid rgba(224, 224, 224, 1)'
    },
    downloadButton: {
        minWidth: '0',
        padding: theme.spacing(1)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const saePermissions = [Dtos.Permission.SaeView, Dtos.Permission.SaeReview, Dtos.Permission.SaeAdminister, Dtos.Permission.MedicalReviewPerform];

const SOURCE_DOCUMENT_NAME = 'document'

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const SaeSubform: React.FunctionComponent<IOcGroup> = ({
    data,
    columns,
    name,
    title,
    titleFormattingOptions,
    entityName,
    createRowProps,
    Before
}) => {
    const classes = useStyles();

    return (
        <div className={classes.mt3}>
            { !!Before && (<Before />)}
            <CollapsibleTable
                title={title}
                titleFormattingOptions={titleFormattingOptions}
                loadState={{ state: RequestState.Success }}
                data={data ?? []}
                columns={columns}
                entityName={entityName ?? 'Record'}
                createRowProps={createRowProps}
            />
        </div>
    );
}

const NoteModal: React.FunctionComponent<INoteModalProps> = ({
    note,
    setNote,
    modalOpen,
    onCloseClick,
    onSave,
}) => {

    const handleChange = React.useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setNote(event.target.value);
    }, [setNote]);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="md"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Notification Not Required Note
                </DialogTitle>
                <DialogContent>
                    <TextField
                        variant="outlined"
                        multiline={true}
                        rows={8}
                        maxRows={25}
                        margin="dense"
                        id="note"
                        value={note}
                        onChange={handleChange}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseClick} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={onSave} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const SaeViewButtons: React.FunctionComponent<ISaeViewButtonsProps> = ({
    saeStatus,
    canViewSae,
    canReviewSae,
    canAdministerSae,
    canMedicallyReviewSae
}) => {
    const classes = useStyles();

    const params = useParams<ISaeViewPageParams>();

    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [note, setNote] = React.useState<string>('');

    const [institution] = React.useContext(InstitutionContext);

    const [sae, , saeActions, ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const notificationNotRequiredClick = React.useCallback(() => {
        saeActions.performAction(new Dtos.WebAction({
            patientStudyNumber: sae?.patient.studyNumber,
            saeNumber: sae?.form.saeNumber,
            saeFollowUpNumber: sae?.form.followUpNumber,
            actionType: Dtos.SaeWebActionTypeEnum.ApprovalNotRequired,
            notes: note,
            recipients: ''
        }));
        saeActions.load();
    }, [sae, saeActions, note]);

    const resetSaeClick = React.useCallback(() => {
        saeActions.performAction(new Dtos.WebAction({
            patientStudyNumber: sae?.patient.studyNumber,
            saeNumber: sae?.form.saeNumber,
            saeFollowUpNumber: sae?.form.followUpNumber,
            actionType: Dtos.SaeWebActionTypeEnum.Reset,
            notes: '',
            recipients: ''
        }));

        saeActions.load();
    }, [sae, saeActions]);

    const closeNoteModal = React.useCallback(() => {
        setModalOpen(false);
        setNote('');
    }, [setModalOpen, setNote]);

    return <>
        <div className={classes.buttonSet}>
            {
                !!canAdministerSae &&
                saeStatus != null &&
                [Dtos.SaeStatus.Added, Dtos.SaeStatus.New, Dtos.SaeStatus.AwaitingForReview].includes(saeStatus) &&
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        component={Link}
                        to={`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}/notify/investigators`}
                    >
                        Notify Investigators
                    </Button>
            }

            {
                !!canMedicallyReviewSae &&
                sae?.actionHistory
                    .find(a => a.actionType === Dtos.SaeWebActionTypeEnum.NotifiedMedicalReviewers || a.actionType === Dtos.SaeWebActionTypeEnum.SubmittedMedicalReview)?.actionType === Dtos.SaeWebActionTypeEnum.NotifiedMedicalReviewers &&
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    component={Link}
                    to={`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}/mreview`}
                >
                    Medical Review
                </Button>
            }

            {
                !!canAdministerSae &&
                saeStatus != null &&
                saeStatus !== Dtos.SaeStatus.Invalid &&
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    component={Link}
                    to={`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}/notify/mreviewer`}
                >
                    Notify Medical Reviewers
                </Button>
            }

            {
                !!canAdministerSae &&
                saeStatus != null &&
                [Dtos.SaeStatus.Added, Dtos.SaeStatus.New].includes(saeStatus) &&
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={() => setModalOpen(true)}
                        >
                            Notification Not Required
                        </Button>
                        <NoteModal 
                            note={note}
                            setNote={setNote}
                            modalOpen={modalOpen}
                            onCloseClick={closeNoteModal}
                            onSave={notificationNotRequiredClick}
                        />
                    </>
            }

            {
                !!canAdministerSae &&
                saeStatus != null &&
                [Dtos.SaeStatus.Approved, Dtos.SaeStatus.Rejected, Dtos.SaeStatus.Incorrect, Dtos.SaeStatus.AwaitingForReview].includes(saeStatus) &&
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.button}
                        onClick={resetSaeClick}
                    >
                        Reset SAE Status
                    </Button>
            }

            {
                !!canAdministerSae &&
                saeStatus != null &&
                saeStatus !== Dtos.SaeStatus.Invalid &&
                <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    component={Link}
                    to={`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}/invalid`}
                >
                    Mark as Invalid
                </Button>
            }
        </div>
    </>;
};

const SaeInvestigatorReviewForm: React.FunctionComponent<ISaeViewButtonsProps> = ({
    saeStatus,
    canAdministerSae,
    canViewSae,
    canMedicallyReviewSae
}) => {
    const classes = useStyles();

    const history = useHistory();

    const params = useParams<ISaeViewPageParams>();

    const [institution] = React.useContext(InstitutionContext);

    const [sae, , saeActions, ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const initialInvestigatorReviewForm: IRejectionForm = {
        reason: '',
        rejectedAs: undefined,
        followUpNumber: undefined
    };

    const formSubscriptionParams = {
        submitting: true,
        pristine: false,
        submitError: true,
        dirtySinceLastSubmit: true,
        hasValidationErrors: true,
        valid: true,
        validating: true,
        errors: true,
        form: true
    };

    const validation = Yup.object().shape<IRejectionForm>({
        reason: Yup.string().required().label('Reason for rejection'),
        rejectedAs: Yup.mixed().required().label('Rejection as'),
    });

    const { enqueueSnackbar } = useSnackbar();

    const approveSaeClick = React.useCallback(() => {
        saeActions.performAction(new Dtos.WebAction({
            patientStudyNumber: sae?.patient.studyNumber,
            saeNumber: sae?.form.saeNumber,
            saeFollowUpNumber: sae?.form.followUpNumber,
            actionType: Dtos.SaeWebActionTypeEnum.Approved,
            notes: '',
            recipients: ''
        }));


        enqueueSnackbar('Investigator review has been successfully submitted.', { variant: 'success' });
        //history.push(`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}`);
    }, [sae, saeActions, enqueueSnackbar]);

    const onRejectionFormSave = React.useCallback(() => {
        enqueueSnackbar('Investigator review has been successfully submitted.', { variant: 'success' });
        history.push(`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}`);
    }, [institution?.code, params.patientStudyNumber, params.saeNumber, enqueueSnackbar, history]);

    const rejectionForm = React.useCallback((props: FormRenderProps<IRejectionForm>) => {
        return (
            <RejectionForm
                onFormSave={onRejectionFormSave}
                {...props}
            />
        );
    }, [onRejectionFormSave]);


    return <>
        {
            sae?.status !== Dtos.SaeStatus.Approved &&
            sae?.status !== Dtos.SaeStatus.Rejected &&
            <div className={`${classes.mt3} ${classes.mb3}`}>
                <Typography component={'span'}>
                    An SAE has been updated for the Neo-N trial.

                    <List component="ul">
                        <ListItem>Approve: If submitted data is correct click 'approve'</ListItem>
                        <ListItem>Reject: If data amendments are required or the event does not constitute an SAE enter the reason and click ‘reject’. The site ISC will be requested to log in to OpenClinica to make the required changes or submit a follow up SAE.</ListItem>
                    </List>
                </Typography>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={approveSaeClick}
                >
                    Approve SAE
                </Button>
                <br /><br />
                OR
                <br />
                {
                    <AsyncForm
                        initialValues={initialInvestigatorReviewForm}
                        validate={validation}
                        onSubmit={saeActions.asyncReject}
                        formSubscription={formSubscriptionParams}
                        validationDebounceTimeout={200}
                        component={rejectionForm}
                        mutators={{
                            ...arrayMutators
                        }}
                    />
                }
            </div>
        }
    </>;
};

const RejectionForm: React.FunctionComponent<IRejectionFormProps> = ({
    onFormSave,
    ...formRenderProps
}) => {
    const classes = useStyles();

    const params = useParams<ISaeViewPageParams>();

    const [sae, , , ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const { enqueueSnackbar } = useSnackbar();

    const onRejectionFormSubmit = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to submit the rejection form.', { variant: 'error' });
        }
    }, [formRenderProps, enqueueSnackbar, onFormSave]);

    const { subscribe } = useForm()

    React.useEffect(() => {
        const unsubscribe = subscribe((formState) => {}, { values: true });

        return () => unsubscribe();
    }, [subscribe])

    const lookups: ILookup = {
        propertyName: "rejectedAs",
        items: [
            {id: 1, value: "SAE is invalid"},
            {id: 2, value: "Information is incorrect"}
        ]
    }

    return (
        <>
            <Grid item xs={12} className={classes.mt1}>
                <Typography
                    variant="h6"
                >
                    Rejected as:
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <LookupContext.Provider value={lookups}>
                <Field
                    name="rejectedAs"
                    component={ToggleButtonGroupWrapper}
                />
                </LookupContext.Provider>
            </Grid>

            <Grid item xs={12} className={classes.mt1}>
                <Typography
                    variant="h6"
                >
                    Reason for Rejection:
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Field
                    fullWidth
                    name="reason"
                    component={TextFieldWrapper}
                    type="text"
                    rows={10}
                    multiline={true}
                    variant="outlined"
                />
            </Grid>
            <Field
                name="followUpNumber"
                className={classes.hidden}
                component="input"
                defaultValue={sae?.form.followUpNumber}
            />
            <Button
                variant="contained"
                color="primary"
                onClick={onRejectionFormSubmit}
                className={classes.mt3}
            >
                Reject SAE
            </Button>
        </>
    )
}

const SaeViewPage: React.FunctionComponent<ISaeViewPageProps> = () => {
    const classes = useStyles();

    const history = useHistory();

    const params = useParams<ISaeViewPageParams>();

    const [institution] = React.useContext(InstitutionContext);

    const [[canViewSae, canReviewSae, canAdministerSae, canMedicallyReviewSae], saePermissionLoadState] = useBctApiPermissions(saePermissions);

    const [sae, saeLoadState, saeActions, saeActionState] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    React.useEffect(() => {
        saeActions.load();

        return () => {
            saeActions.clear();
        }
    }, [saeActions, params.viewMode]);

    React.useEffect(() => {
        if (saeActionState.state === RequestState.Success ) {
            history.push(`/sae/${institution?.code}/${params.patientStudyNumber}/${params.saeNumber}`);
            saeActions.load();
        }

        return () => {
            saeActions.clear();
        }
    }, [saeActions, saeActionState, history, institution?.code, params.patientStudyNumber, params.saeNumber]);

    React.useEffect(() => {
        if (saePermissionLoadState.state !== RequestState.Pending &&
            saePermissionLoadState.state !== RequestState.None) {
            if (!canViewSae) {
                history.replace(`/error/403`);
            }
        }
    }, [institution, canViewSae, canReviewSae, params, saePermissionLoadState, history]);

    if (saeLoadState.state === RequestState.None || saeLoadState.state === RequestState.Pending) {
        return <RouteLoading />;
    }

    return (
        <PageLayout
            breadcrumbs={
                <SaeBreadcrumbs />
            }
            heading="Serious Adverse Event"
        >
            <Paper
                className={classes.container}
            >
                <SaeInformation
                    sae={sae}
                />

                {
                    !params.viewMode &&
                    <SaeViewButtons
                        saeStatus={sae?.status}
                        canViewSae={canViewSae}
                        canReviewSae={canReviewSae}
                        canAdministerSae={canAdministerSae}
                        canMedicallyReviewSae={canMedicallyReviewSae}
                    />
                }

                {
                    params.viewMode === 'review' &&
                    !!canReviewSae &&
                    <SaeInvestigatorReviewForm
                        saeStatus={sae?.status}
                        canAdministerSae={canAdministerSae}
                        canViewSae={canViewSae}
                        canMedicallyReviewSae={canMedicallyReviewSae}
                    />
                }

                {
                    params.viewMode === 'mreview' &&
                    !!canMedicallyReviewSae &&
                    <>
                        <SaeHistory
                            patientStudyNumber={params.patientStudyNumber}
                            institutionCode={institution?.code}
                            saeNumber={sae?.form.saeNumber}
                        />
                        <SMRForm
                            patientStudyNumber={params.patientStudyNumber}
                            saeNumber={params.saeNumber}
                            viewMode={params.viewMode}
                        />
                    </>
                }

                {
                    sae?.status !== Dtos.SaeStatus.Invalid && params.viewMode !== 'invalid' ?
                        <ValidSaeView
                            canAdministerSae={canAdministerSae}
                            canMedicallyReviewSae={canMedicallyReviewSae}
                        />
                        :
                        <InvalidSaeForm
                            patientStudyNumber={sae?.patient?.studyNumber}
                            saeNumber={sae?.form?.saeNumber}
                            readOnly={!canAdministerSae}
                        />
                }
            </Paper>
        </PageLayout>
    )
}

const ValidSaeView: React.FunctionComponent<IInvalidSaeFormProps> = ({
    canAdministerSae,
    canMedicallyReviewSae
}) => {
    const classes = useStyles();

    const params = useParams<ISaeViewPageParams>();

    const [sae, , , ] = useSaeById(
        params.patientStudyNumber!!,
        +params.saeNumber!!
    );

    const groups: IOcGroup[] = React.useMemo(() => {
        if (!sae) {
            return [];
        }

        const g: IOcGroup[] = [];

        Object
            .keys(sae.saeFormItems)
            .filter(key => key.includes('.'))
            .forEach(key => {
                const index = key.indexOf('.');
                const groupName = key.substring(0, index);

                const itemName = key.substring(index + 1);

                let group = g.find(x => x.name === groupName)

                if (!group) {

                    group = { title: sae.saeFormItems[groupName], name: groupName, data: sae.form[groupName] ?? sae[groupName], columns: [] };

                    g.push(group);
                }

                group.createRowProps = (row, rowProps) => {

                    let ecrfIndex: number = 0;

                    sae.ecrfs
                        .forEach((ecrf, i) => {
                            if (ecrf[groupName]?.length > 0) {
                                ecrfIndex = i;
                                return;
                            }
                        });

                    return {
                        className: classNames({ [classes.blueBgColor]: sae.ecrfs[ecrfIndex][groupName]?.length > row.index && ecrfIndex !== 0, [classes.row]: sae.ecrfs[ecrfIndex][groupName]?.length <= row.index })
                    }
                };

                group.columns.push({
                    id: key,
                    accessor: itemName,
                    Header: sae.saeFormItems[key],
                    Cell: (props) => {

                        if (!!sae.form && !!sae.ecrfs) {
                            if (itemName === SOURCE_DOCUMENT_NAME) {
                                return (
                                    <div className={`${classes.cellHeight}`}>
                                        <Tooltip title="Download">
                                            <Button 
                                                color="primary"
                                                variant="contained"
                                                className={classes.downloadButton}
                                                component="a"
                                                target="_blank"
                                                href={`/api/sae/open-clinica/document?FileName=${encodeURIComponent(props.cell?.value)}`}
                                                disabled={!props.cell?.value}
                                            >
                                                <FontAwesomeIcon icon={faDownload} />
                                            </Button>
                                        </Tooltip>
                                    </div>
                                );
                            } else {
                                return (
                                    <div className={classes.cellHeight}>
                                        <div dangerouslySetInnerHTML={{ __html: getTransformedValue(props.cell?.value) ?? '' }} />
                                    </div>
                                );
                            }
                        }

                        return undefined;
                    }
                })
            });

        g.push()

        return g;
    }, [sae, classes.blueBgColor, classes.cellHeight, classes.downloadButton, classes.row]);

    const treatmentLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, treatmentPropertyPath);
    }, [sae]);

    const relatednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, relatednessPropertyPath);
    }, [sae]);

    const expectednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, expectednessPropertyPath);
    }, [sae]);

    const medicalReviewRelatednessLookup = React.useMemo(() => {
        return GetFieldLookup(sae?.lookups!, medicalReviewRelatednessPropertyPath);
    }, [sae]);

    const medicalReviewGroup: IOcGroup | null = React.useMemo(() => {
        if (!sae?.medicalReview) {
            return null;
        }

        return {
            title: 'Medical Review',
            titleFormattingOptions: {
                plural: false,
                lowercase: false
            },
            name: 'medicalReview.items',
            entityName: 'Study Drug',
            data: sae?.medicalReview?.items ?? [],
            Before: (sae?.medicalReview?.date ?? '') < (sae?.form?.dateUpdated ?? '') ?
                () => {
                    return (
                        <Alert
                            icon={<FontAwesomeIcon icon={faInfoCircle} fixedWidth />}
                            severity="info"
                        >
                            The SAE has been updated since this medical review was submitted.
                        </Alert>
                    );
                } :
                undefined,
            columns: [
                {
                    id: 'medicalReview.items.typeId',
                    accessor: 'typeId',
                    Header: 'Study Drug',
                    Cell: props => <div className={classes.cellHeight}>
                        {treatmentLookup?.items?.find(li => li.id === props.cell?.value)?.value}
                    </div>
                },
                {
                    id: 'medicalReview.items.latestRelatednessId',
                    accessor: 'latestRelatednessId',
                    Header: 'Site Investigator Relatedness Assessment',
                    Cell: props => <div className={classes.cellHeight}>
                        {relatednessLookup?.items?.find(li => li.id === props.cell?.value)?.value ?? 'Not Provided'}
                    </div>
                },
                {
                    id: 'medicalReview.items.relatednessId',
                    accessor: 'relatednessId',
                    Header: 'Relatedness Assessment',
                    Cell: props => <div className={classes.cellHeight}>
                        {!!props.row.original.notApplicable ? 'Not Applicable' : medicalReviewRelatednessLookup?.items?.find(li => li.id === props.cell?.value)?.value}
                    </div>
                },
                {
                    id: 'medicalReview.items.expected',
                    accessor: 'expected',
                    Header: 'Expectedness',
                    Cell: props => <div className={classes.cellHeight}>
                        {expectednessLookup?.items?.find(li => li.id === props.cell?.value)?.value}
                    </div>
                },
                {
                    id: 'medicalReview.items.comments',
                    accessor: 'comments',
                    Header: 'Comments/Clinical Query',
                    Cell: props => <div className={classes.cellHeight}>
                        <pre style={{ whiteSpace: 'pre-line', wordBreak: 'break-word', background: 'transparent', border: 'none', textAlign: 'left', display: 'inline-block', marginBottom: 0, paddingBottom: 0 }}>
                            {props.cell?.value}
                        </pre>
                    </div>
                }
            ],
            createRowProps: undefined
        }
    }, [sae, classes.cellHeight, expectednessLookup?.items, medicalReviewRelatednessLookup?.items, relatednessLookup?.items, treatmentLookup?.items])

    return (
        <>
            {
                sae?.medicalReview && sae?.form.saeNumber && <SaeNarrativeSummary
                    patientStudyNumber={sae.patient.studyNumber}
                    saeNumber={sae?.form.saeNumber}
                    canAdministerSae={canAdministerSae === true}
                />
            }

            <div className={classes.legendContainer}>
                <div className={classes.legend} />

                <Typography
                    variant="h6"
                    color="inherit"
                    className={classes.legendInfo}
                >
                    {'Latest value updated in the latest follow up'}
                </Typography>
            </div>

            <Typography
                variant="h2"
                color="primary"
                className={classes.mt1}
            >
                Current Serious Adverse Event Entry
            </Typography>

            <TableContainer className={classes.table} component={Paper} elevation={3}>
                <Table>
                    <TableBody>
                        {
                            !!sae && Object.keys(sae.saeFormItems!!)?.map((itemKey) => {
                                return renderItem(itemKey,
                                    groups,
                                    sae,
                                    false,
                                    classes);
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            {
                sae?.ecrfs && sae?.ecrfs.length > 1 &&
                <>
                    <Typography
                        variant="h2"
                        color="primary"
                        className={classes.mt4}
                    >
                        Initial Serious Adverse Event Entry
                    </Typography>
                    <TableContainer className={classes.table} component={Paper} elevation={3}>
                        <Table>
                            <TableBody>
                                {
                                    !!sae && Object.keys(sae.saeFormItems!!)?.map((itemKey) => {
                                        return renderItem(itemKey,
                                            groups,
                                            sae,
                                            true,
                                            classes);
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            }

            {
                !!sae && !!groups && groups.map((group, groupNumber) => {
                    if (groupNumber === groups.length - 1) {
                        return null;
                    }

                    return <SaeSubform key={group.name} {...group} />
                })
            }
            {
                !!sae && canMedicallyReviewSae && !!medicalReviewGroup && (
                    <SaeSubform {...medicalReviewGroup} />
                )
            }
            {
                !!sae && !!groups && groups.length >= 1 && (
                    <SaeSubform {...groups[groups.length - 1]} />
                )
            }
        </>
    );
}

const showItem = (itemKey: string,
    groups: IOcGroup[]): boolean => {
    if (itemKey.includes('.')) {
        //don't show items for groups
        return false;
    }
    else if (groups && groups.some(g => g.name === itemKey)) {
        //if the item key is a group heading, don't show it
        return false;
    }

    return true;
}

const renderItem = (itemKey: string,
    groups: IOcGroup[],
    sae: Dtos.Sae<Dtos.SaeForm, Dtos.Patient>,
    isInitial: boolean,
    classes: ClassNameMap<string>) => {

    //render out item values. this would be better as a function component i guess?
    if (!showItem(itemKey, groups)) {
        return null;
    }

    const className = isInitial ?
        classes.row :
        (hasChanged(sae.ecrfs, itemKey) ? classes.blueBgColor : classes.row);

    const displayValue = isInitial ?
        getTransformedValue(sae.ecrfs[0][itemKey]) :
        getTransformedValue(sae.form[itemKey]);

    return <TableRow key={itemKey} className={className}>
        <TableCell className={classes.tableHeader} component="th" scope="row">
            {sae.saeFormItems[itemKey]}
        </TableCell>
        <TableCell align="left">
            <div dangerouslySetInnerHTML={{ __html: displayValue ?? '' }} />
        </TableCell>
    </TableRow>
}

const hasChanged = (saes: Dtos.SaeForm[], itemKey: string): boolean => {
    if (saes.length < 2) {
        return false;
    }

    if (getTransformedValue(saes[saes.length - 1][itemKey]) !== '' && getTransformedValue(saes[saes.length - 1][itemKey]) !== getTransformedValue(saes[saes.length - 2][itemKey])) {
        return true;
    }

    return false;
}

const getTransformedValue = (property: any): string | null => {

    if (property == null) {
        return '';
    }

    if (typeof property === 'boolean') {
        return (property === true ? 'Yes' : 'No');
    } else if (typeof property === 'string') {
        if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{7}Z/.test(property)) {
            return DateTime.fromISO(property).toFormat('dd/MM/yyyy, hh:mm a');
        } else if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{7}/.test(property)) {
            return DateTime.fromISO(property).toFormat('dd/MM/yyyy');
        }
    } else if (typeof property === 'object') {
        // For lookup values
        return property.value;
    }

    return property?.toString().replace(/<br\/><br\/>$/, '');
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaeViewPage;
